// FILE: src/pages/admin/AdminExportsPage.jsx
// Records & exports (WP-7; MTO 11.4, 11.14; matrix 11.4, 11.14), two pages:
//   /admin/exports              every student's record for a date range, in
//                               bulk: GET /api/admin/records/export
//                               ?from&to (Toronto days, to included) &format=csv|json
//                               → student-records-<from>-to-<to>.<ext>
//   /admin/exports/daily-hours  credited learning time per student per day
//                               (AdminDailyHoursPage.jsx)
// Both are audited on the server (RECORDS_BULK_EXPORTED, DAILY_HOURS_EXPORTED).
import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { PageHeader, Section } from "../../components/ui";
import SubNav from "../../components/admin/SubNav";
import { downloadFromApi, formatBytes } from "../../components/admin/download";
import { rangeProblem, torontoDay, torontoDaysAgo } from "../../components/admin/dates";
import { errorMessage } from "../../i18n/errors";

export const EXPORT_SECTIONS = [
  { label: "Student records", path: "/admin/exports" },
  { label: "Daily learning hours", path: "/admin/exports/daily-hours" }
];

/** Header + section links shared by the two export pages. */
export function ExportsShell({ title, description, children }) {
  const { pathname } = useLocation();
  const current = EXPORT_SECTIONS.find((s) => s.path === pathname.replace(/\/$/, "")) || EXPORT_SECTIONS[0];
  return (
    <Box>
      <PageHeader
        title={title}
        description={description}
        breadcrumbs={[{ label: "Records & exports", to: "/admin/exports" }, { label: current.label }]}
      />
      <SubNav label="Records and exports" items={EXPORT_SECTIONS} current={current.path} />
      {children}
    </Box>
  );
}

const INCLUDED = [
  "One row per student: name, email, registration number, licence, enrolment dates and whether the enrolment has ended",
  "Where they are: progress status, chapters and homelinks completed, the date the course was finished, locked or untimed",
  "Credited time in total and within the dates, core (chapters) and flex (homelinks) hours separate, days at the 5-hour limit",
  "Number of quiz attempts and the average score, when the survey was sent and how many support requests they made",
  "A second table with each student's credited time for every day in the range (Ontario time)"
];

export default function AdminExportsPage() {
  const [draft, setDraft] = useState(() => ({ from: torontoDaysAgo(30), to: torontoDay(), format: "csv" }));
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const bad = rangeProblem(draft.from, draft.to);
    setProblem(bad);
    if (bad) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const q = new URLSearchParams({ from: draft.from, to: draft.to, format: draft.format });
      const file = await downloadFromApi(`/api/admin/records/export?${q.toString()}`, {
        fallbackName: `student-records-${draft.from}-to-${draft.to}.${draft.format}`
      });
      setResult(file);
    } catch (err) {
      setError(err || "INTERNAL_ERROR");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ExportsShell
      title="Student records export"
      description="Every student's record for a date range, in one file, for the Ministry or for your own records (MTO 11.4)."
    >
      <Stack spacing={2.5}>
        <Section
          id="bulk-export"
          title="Choose the dates"
          description="Includes every student who was added, started their enrolment or had learning time credited between these dates, including locked accounts and ended enrolments."
        >
          <Box component="form" noValidate onSubmit={submit}>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "flex-start" } }}>
                <TextField
                  id="export-from"
                  label="From"
                  type="date"
                  value={draft.from}
                  onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                  error={!!problem}
                  required
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: draft.to || undefined } }}
                />
                <TextField
                  id="export-to"
                  label="To"
                  type="date"
                  value={draft.to}
                  onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                  error={!!problem}
                  required
                  helperText="Includes this day (Ontario time)"
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: draft.from || undefined } }}
                />
              </Stack>
              <FormControl>
                <FormLabel id="export-format-label" sx={{ fontWeight: 600, color: "text.primary" }}>
                  File type
                </FormLabel>
                <RadioGroup
                  aria-labelledby="export-format-label"
                  name="export-format"
                  value={draft.format}
                  onChange={(e) => setDraft((d) => ({ ...d, format: e.target.value }))}
                >
                  <FormControlLabel value="csv" control={<Radio />} label="CSV, to open in a spreadsheet" />
                  <FormControlLabel value="json" control={<Radio />} label="JSON, a data file for other systems" />
                </RadioGroup>
              </FormControl>
              {problem && (
                <Alert severity="error" role="alert">
                  {problem}
                </Alert>
              )}
              {error && (
                <Alert severity="error" role="alert">
                  <AlertTitle>We couldn't make the export</AlertTitle>
                  {errorMessage(error)}
                </Alert>
              )}
              {result && (
                <Alert severity="success" role="status" data-testid="export-result">
                  <AlertTitle>Export downloaded</AlertTitle>
                  Saved as <strong>{result.filename}</strong>
                  {result.size != null ? ` (${formatBytes(result.size)})` : ""}. The download is recorded in the audit log.
                </Alert>
              )}
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={busy}
                  startIcon={busy ? <CircularProgress size={18} color="inherit" aria-hidden /> : <FileDownloadOutlinedIcon />}
                >
                  {busy ? "Preparing the export…" : "Download records"}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Section>

        <Section id="export-contents" title="What's in the file">
          <Box component="ul" sx={{ m: 0, pl: 3, display: "grid", gap: 0.75 }}>
            {INCLUDED.map((c) => (
              <Typography component="li" key={c}>
                {c}
              </Typography>
            ))}
          </Box>
          <Typography sx={{ mt: 2 }}>
            For one student's complete record (every sign-in, session, message and score), open the student and choose
            Records. For learning time only, see{" "}
            <Link component={RouterLink} to="/admin/exports/daily-hours">
              daily learning hours
            </Link>
            . Records are kept for 7 years after the course ends.
          </Typography>
        </Section>
      </Stack>
    </ExportsShell>
  );
}
